import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Calendar } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { submitWorkshopRegistration } from "@/lib/api";

const defaultForm = {
  prenom: "",
  nom: "",
  email: "",
  telephone: "",
  workshop_id: "",
  participation_type: "avec_problematique",
  message: "",
};

export default function WorkshopDialog({ open, onOpenChange, workshops, preselected }) {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...defaultForm,
        workshop_id: preselected?.id || f.workshop_id || (workshops?.[0]?.id ?? ""),
      }));
    }
  }, [open, preselected, workshops]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.prenom.trim() ||
      !form.nom.trim() ||
      !form.email.trim() ||
      !form.workshop_id
    ) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await submitWorkshopRegistration({
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        email: form.email.trim(),
        telephone: form.telephone.trim() || null,
        workshop_id: form.workshop_id,
        participation_type: form.participation_type,
        message: form.message.trim() || null,
      });
      toast.success(
        "Votre pré-inscription a bien été enregistrée. Vous serez recontacté(e) pour la confirmation."
      );
      setForm(defaultForm);
      onOpenChange(false);
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="workshop-dialog"
        className="sm:max-w-xl bg-[#FAF7F8] border-[#DDD7E2] rounded-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        <div className="p-7 md:p-8">
          <DialogHeader className="text-left space-y-2">
            <span className="text-xs uppercase tracking-[0.22em] text-[#C98FA3] font-medium">
              — Inscription atelier
            </span>
            <DialogTitle className="font-serif-display text-3xl md:text-4xl font-light text-[#1E1E2A]">
              Réserver votre place
            </DialogTitle>
            <DialogDescription className="text-sm text-[#5F6170]">
              Renseignez vos coordonnées, vous serez recontacté(e) pour la confirmation
              définitive et le règlement.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="workshop-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                  Prénom*
                </label>
                <Input
                  data-testid="workshop-prenom"
                  value={form.prenom}
                  onChange={update("prenom")}
                  placeholder="Votre prénom"
                  className="mt-2 bg-white border-[#DDD7E2] focus-visible:ring-[#2847B8] rounded-xl h-11"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                  Nom*
                </label>
                <Input
                  data-testid="workshop-nom"
                  value={form.nom}
                  onChange={update("nom")}
                  placeholder="Votre nom"
                  className="mt-2 bg-white border-[#DDD7E2] focus-visible:ring-[#2847B8] rounded-xl h-11"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                  Email*
                </label>
                <Input
                  data-testid="workshop-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="votre@email.com"
                  className="mt-2 bg-white border-[#DDD7E2] focus-visible:ring-[#2847B8] rounded-xl h-11"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                  Téléphone
                </label>
                <Input
                  data-testid="workshop-telephone"
                  value={form.telephone}
                  onChange={update("telephone")}
                  placeholder="Facultatif"
                  className="mt-2 bg-white border-[#DDD7E2] focus-visible:ring-[#2847B8] rounded-xl h-11"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                Atelier choisi*
              </label>
              <Select
                value={form.workshop_id}
                onValueChange={(v) => setForm((f) => ({ ...f, workshop_id: v }))}
                disabled={loading}
              >
                <SelectTrigger
                  data-testid="workshop-select"
                  className="mt-2 bg-white border-[#DDD7E2] rounded-xl h-11"
                >
                  <SelectValue placeholder="Choisir un atelier" />
                </SelectTrigger>
                <SelectContent>
                  {(workshops || []).map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.date_label} — {w.lieu}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                Type de participation*
              </label>
              <RadioGroup
                value={form.participation_type}
                onValueChange={(v) => setForm((f) => ({ ...f, participation_type: v }))}
                className="mt-3 grid sm:grid-cols-2 gap-3"
                disabled={loading}
              >
                <label
                  htmlFor="part-prob"
                  className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-[#DDD7E2] hover:border-[#C98FA3] cursor-pointer transition-colors"
                  data-testid="workshop-radio-prob-label"
                >
                  <RadioGroupItem id="part-prob" value="avec_problematique" data-testid="workshop-radio-prob" />
                  <div>
                    <p className="text-sm font-medium text-[#1E1E2A]">Avec problématique</p>
                    <p className="text-xs text-[#5F6170] mt-1">
                      Je viens explorer une situation
                    </p>
                  </div>
                </label>
                <label
                  htmlFor="part-rep"
                  className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-[#DDD7E2] hover:border-[#C98FA3] cursor-pointer transition-colors"
                  data-testid="workshop-radio-rep-label"
                >
                  <RadioGroupItem id="part-rep" value="representant" data-testid="workshop-radio-rep" />
                  <div>
                    <p className="text-sm font-medium text-[#1E1E2A]">Représentant</p>
                    <p className="text-xs text-[#5F6170] mt-1">
                      Je participe au travail du groupe
                    </p>
                  </div>
                </label>
              </RadioGroup>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                Message (facultatif)
              </label>
              <Textarea
                data-testid="workshop-message"
                value={form.message}
                onChange={update("message")}
                placeholder="Une question, un besoin particulier…"
                className="mt-2 bg-white border-[#DDD7E2] focus-visible:ring-[#2847B8] rounded-xl min-h-[90px]"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="workshop-submit"
              className="w-full bg-[#2847B8] text-white hover:bg-[#3A2E6E] disabled:opacity-60 transition-all duration-300 rounded-full h-12 font-medium inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Envoi…
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4" /> Confirmer ma pré-inscription
                </>
              )}
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
